import React from "react";
import { SubCriterionRow } from "./SubCriterionRow";
import type {
	KpiCategory,
	SubCriterionRating,
} from "newui/features/MySuppliers/types/supplier-performance.types";

interface CategoryRatingSectionProps {
	category: KpiCategory;
	previousRatings?: SubCriterionRating[];
	ratings: SubCriterionRating[];
	onCommentChange: (
		categoryId: number,
		subCriterionId: number,
		comment: string
	) => void;
	onScoreChange: (
		categoryId: number,
		subCriterionId: number,
		score: number
	) => void;
}

export const CategoryRatingSection: React.FC<CategoryRatingSectionProps> = ({
	category,
	ratings,
	previousRatings = [],
	onScoreChange,
	onCommentChange,
}) => {
	const getSubCriterionRating = (
		subCriterionId: number
	): SubCriterionRating | undefined => {
		return ratings.find((r) => r.subCriterionId === subCriterionId);
	};

	const getPreviousScore = (
		subCriterionId: number
	): number | null => {
		const prev = previousRatings.find(
			(r) => r.subCriterionId === subCriterionId
		);
		return prev?.score ?? null;
	};

	const ratedCount = category.sub_criteria.filter((sub) =>
		ratings.some((r) => r.subCriterionId === sub.id && r.score > 0)
	).length;

	return (
		<div
			className="mb-6"
			data-testid={`category-section-${category.id}`}
		>
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-base font-semibold text-gray-900">
					{category.name}
				</h3>
				<span className="text-xs text-gray-500">
					{ratedCount} of {category.sub_criteria.length} rated
				</span>
			</div>
			{category.description && (
				<p className="mb-4 text-sm text-gray-600">
					{category.description}
				</p>
			)}
			<div className="space-y-3">
				{category.sub_criteria.map((sub) => {
					const rating = getSubCriterionRating(sub.id);
					return (
						<SubCriterionRow
							key={sub.id}
							subCriterion={sub}
							score={rating?.score ?? null}
							comment={rating?.comment ?? null}
							previousScore={getPreviousScore(sub.id)}
							onScoreChange={(subId, score) =>
								onScoreChange(category.id, subId, score)
							}
							onCommentChange={(subId, comment) =>
								onCommentChange(category.id, subId, comment)
							}
						/>
					);
				})}
			</div>
		</div>
	);
};
